import { useState, useEffect, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plug, Loader2, Copy, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";

const STATES = {
  active: { label: "Pont actif ✓", cls: "bg-green-50 border-green-200 text-green-800", ok: true },
  script_not_seen: { label: "Script à installer — jamais vu", cls: "bg-amber-50 border-amber-200 text-amber-800", ok: false },
  not_installed: { label: "Non activé", cls: "bg-slate-50 border-slate-200 text-slate-700", ok: false },
};

export function EmergentBridgePanel({ siteId }) {
  const [info, setInfo] = useState(null);
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!siteId) return;
    setInfo(null);
    setStatus(null);
    api.get(`/sites/${siteId}/bridge/snippet`).then(({ data }) => setInfo(data)).catch(() => {});
  }, [siteId]);

  const snippet = useMemo(() => {
    if (!info?.snippet || !info?.key) return "";
    return info.snippet
      .split("__API_BASE__").join(process.env.REACT_APP_BACKEND_URL)
      .split("__KEY__").join(info.key);
  }, [info]);

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.get(`/sites/${siteId}/bridge/status`, {
        params: { api_base: process.env.REACT_APP_BACKEND_URL },
      });
      setStatus(data);
      if (data.state === "active") toast.success("Pont Emergent actif ✓");
      else toast.warning(data.message);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test impossible");
    } finally {
      setTesting(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(snippet);
    toast.success("Script copié — collez-le dans le <head> du site (via l'agent Emergent).");
  };

  const st = status ? (STATES[status.state] || STATES.not_installed) : null;

  return (
    <div className="mt-6 border border-slate-200 bg-white rounded-md overflow-hidden" data-testid="emergent-bridge-panel">
      <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div className="overline flex items-center gap-2">
          <Plug className="w-3.5 h-3.5" /> Pont Emergent — sites React / SPA
        </div>
        <button
          onClick={test}
          disabled={testing}
          data-testid="bridge-status-button"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-[#002FA7] border border-[#002FA7]/30 rounded-md px-3 py-1.5 hover:bg-blue-50"
        >
          {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plug className="w-3.5 h-3.5" />} Tester le pont
        </button>
      </div>
      <div className="p-5">
        <p className="text-sm text-slate-600">
          Pour les sites faits avec Emergent (React), les sites <strong>Wix</strong> (via Paramètres → Code personnalisé)
          ou tout site où vous pouvez coller un script : les corrections
          (Titre, Méta description, Canonical, Open Graph, Schema) sont enregistrées ici et servies instantanément par
          un petit script installé une fois sur le site — <strong>sans redéploiement</strong> à chaque correction.
          Sur Wix, les articles de blog et les pages gérées nativement (API Wix / GitHub) restent corrigés à la source.
        </p>

        {st && (
          <div className={`mt-3 text-sm rounded-md px-3 py-2 border ${st.cls}`} data-testid="bridge-status-result">
            <div className="flex items-start gap-2">
              {st.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
              <div>
                <div className="font-semibold" data-testid="bridge-state-label">{st.label}</div>
                <div className="mt-0.5">{status.message}</div>
                {status.state === "active" && (
                  <div className="mt-1 text-xs">
                    {status.overrides_count} override(s) actif(s)
                    {status.last_fetch_at && <> · dernier contact : {new Date(status.last_fetch_at).toLocaleString("fr-FR")}</>}
                  </div>
                )}
                {status.endpoint_check && (
                  <div className="mt-1 text-xs" data-testid="bridge-endpoint-check">
                    Endpoint public : {status.endpoint_check.reachable
                      ? <span className="text-green-700">joignable ✓ ({status.endpoint_check.detail})</span>
                      : <span className="text-red-700">indisponible ✕ ({status.endpoint_check.detail})</span>}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <button
          onClick={() => setOpen(!open)}
          data-testid="bridge-instructions-toggle"
          className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#002FA7] hover:underline"
        >
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          Instructions d'installation + script
        </button>

        {open && info && (
          <div className="mt-3">
            <ol className="list-decimal ml-5 space-y-2 text-sm text-slate-700">
              {info.steps.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
            <div className="mt-4 flex items-center justify-between">
              <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Script à coller (clé incluse)</div>
              <button
                onClick={copy}
                data-testid="bridge-copy-snippet"
                className="inline-flex items-center gap-1 text-xs text-[#002FA7] hover:underline"
              >
                <Copy className="w-3 h-3" /> Copier
              </button>
            </div>
            <pre className="mt-2 bg-slate-950 text-slate-100 text-xs rounded-md p-4 overflow-x-auto whitespace-pre max-h-80" data-testid="bridge-snippet-code">{snippet}</pre>
            <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              ⚠️ Honnêteté : les balises sont injectées côté navigateur. <strong>Google les lit</strong> (rendu
              JavaScript), mais le vérificateur HTML source ne peut pas les prouver sur une SPA, et les aperçus
              sociaux (Facebook/WhatsApp/LinkedIn) n'exécutent pas le JavaScript — pour les partages sociaux,
              gardez og:image dans le HTML source du site.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
